import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'user' | 'marketing' | 'marketing_user' | 'marketing_manager' | 'admin';
  isPhoneVerified: boolean;
  isEmailVerified: boolean;
  profileCompleted: boolean;
  isAffiliateEnabled?: boolean;
  referralCode?: string;
  subscription?: Record<string, unknown>;
  adminVerification?: {
    status: 'pending' | 'approved' | 'rejected';
    rejectionReason?: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  register: (userData: Record<string, unknown>) => Promise<Record<string, unknown>>;
  login: (email: string, password: string) => Promise<Record<string, unknown>>;
  truecallerLogin: (data: Record<string, unknown>) => Promise<Record<string, unknown>>;
  sendOTP: (phone?: string) => Promise<Record<string, unknown>>;
  verifyOTP: (otp: string) => Promise<Record<string, unknown>>;
  sendOTPForLogin: (phone: string) => Promise<Record<string, unknown>>;
  verifyOTPForLogin: (phone: string, otp: string) => Promise<Record<string, unknown>>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};